import { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Link } from 'react-router-dom';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import TitleReveal from '@/components/base/TitleReveal';
import { services, serviceCutouts } from '@/mocks/services';

export default function ServiceSelector() {
  const [active, setActive] = useState(0);
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);
  const firstRun = useRef(true);
  const hoverTimer = useRef<number | null>(null);
  const queueActive = (i: number) => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setActive(i), 90);
  };
  const cancelQueue = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
  };

  useLayoutEffect(() => {
    const imgs = imgRefs.current.filter(Boolean) as HTMLImageElement[];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gsap.killTweensOf(imgs);

    if (firstRun.current || reduced) {
      firstRun.current = false;
      imgs.forEach((img, i) => gsap.set(img, { opacity: i === active ? 1 : 0, x: 0, scale: 1, zIndex: i === active ? 2 : 1 }));
      return;
    }

    imgs.forEach((img, i) => {
      if (i === active) {
        gsap.set(img, { zIndex: 2 });
        gsap.fromTo(img, { opacity: 0, xPercent: -60, scale: 0.96 }, { opacity: 1, xPercent: 0, scale: 1, duration: 0.9, delay: 0.4, ease: 'expo.out' });
      } else {
        gsap.set(img, { zIndex: 1 });
        gsap.to(img, { opacity: 0, xPercent: 25, duration: 0.35, ease: 'power2.in' });
      }
    });
  }, [active]);

  useLayoutEffect(() => () => {
    gsap.killTweensOf(imgRefs.current.filter(Boolean));
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
  }, []);

  return (
    <section id="services" className="bg-background-50 py-20 md:py-32">
      <Container>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="order-2 lg:order-1">
            <div className="hidden lg:block mb-10"><Eyebrow label="What We Repair" /></div>
            <div className="relative h-[380px] sm:h-[480px] lg:h-[640px] w-full overflow-hidden">
              {services.map((s, i) => (
                <img
                  key={s.slug}
                  ref={(el) => { imgRefs.current[i] = el; }}
                  src={serviceCutouts[s.slug] || s.image}
                  alt={`${s.name} by MENDWELL`}
                  title={`${s.name} - in-home appliance repair`}
                  className={`absolute inset-0 h-full w-full object-contain object-center will-change-[opacity,transform] ${i === active ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
            </div>
          </div>

          <div className="order-1 lg:order-2 flex flex-col">
            <div className="lg:hidden mb-6"><Eyebrow label="What We Repair" /></div>
            <TitleReveal className="text-[34px] md:text-[48px] lg:text-[54px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">
              Everything Your Home Relies On
            </TitleReveal>
            <p className="mt-6 max-w-md text-[15px] md:text-base leading-relaxed text-foreground-600">
              From the fridge to the laundry room, we repair the everyday appliances that quietly keep your household moving.
            </p>

            <div className="mt-10 lg:mt-auto lg:pt-12 border-l border-background-200" onMouseLeave={cancelQueue}>
              {services.map((s, i) => {
                const isActive = i === active;
                return (
                  <div key={s.slug} onMouseEnter={() => queueActive(i)} className={`relative pl-6 md:pl-10 before:absolute before:-left-px before:top-0 before:bottom-0 before:w-px before:bg-primary-500 before:origin-top before:transition-transform before:duration-700 before:ease-[cubic-bezier(0.22,1,0.36,1)] ${isActive ? 'before:scale-y-100' : 'before:scale-y-0'}`}>
                    <div className="border-b border-background-200 py-5">
                      <div className="flex items-center justify-between gap-4">
                        <button
                          type="button"
                          onClick={() => { cancelQueue(); setActive(i); }}
                          className={`cursor-pointer text-left text-lg md:text-[22px] font-light tracking-tight transition-[color,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isActive ? 'text-foreground-950 translate-x-1.5' : 'text-foreground-700 hover:text-foreground-950'}`}
                        >
                          {s.name}
                        </button>
                        <Link
                          to={`/services/${s.slug}`}
                          aria-label={`View ${s.name}`}
                          className={`flex h-10 w-10 md:h-11 md:w-11 shrink-0 items-center justify-center rounded-full transition-[background-color,border-color,color,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isActive ? 'bg-primary-500 text-white' : 'border border-background-200 text-primary-600 hover:border-primary-300'}`}
                        >
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                      <div className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                        <div className="overflow-hidden">
                          <p className={`pt-3 max-w-sm text-sm md:text-[15px] leading-relaxed text-foreground-600 transition-[opacity,transform] duration-500 ease-out ${isActive ? 'opacity-100 translate-y-0 delay-100' : 'opacity-0 -translate-y-1'}`}>{s.summary}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}